import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { getProductDetailsFromContext, getProductFromContext } from '../../../../context/product';
import VariantSelector from '../VariantSelector';
import AddToCartButtonContainer from './AddToCartButtonContainer';
import AddToCartButtonFloatingContainer from './AddToCartButtonFloatingContainer';
import { addProduct, showCart } from '../../../../store/store.actions';
import { CartEventLocation, ProductTag, ProductStage, SizeSelectionLocation } from '../../../../types/common';
import {
  analyticsTrackAddToCart,
  analyticsTrackViewCart,
  sendAddUnselectedVariantToCartEvent,
  analyticsTrackSizeSelected,
} from '../../../../utils/analytics';
import { getQtyOfProductInCart } from '../../../../utils/cart';
import {
  getStage,
  hasProductTag,
  isApparelProduct,
  parseMetafields,
  isOneSizeApparelProduct,
} from '../../../../utils/product';
import { useStore } from '../../../../store';
import SpamFlaggingContext from '../../../../context/spamFlagging';
import { SizeSelector } from '../../../../ui/molecules/SizeSelector';
import { SelectOption } from '../../../../ui/atoms/Select';
import { addVariantSelectionErrorIfNotAlreadyPresent } from '../../../../utils/errors';
import { ErrorType, VariantSelectionError } from '../../../../types/errors';
import {
  variantsToSelectOptions,
  variantToSelectOption,
  hasApparelSizeError,
  findVariantBySelectOption,
} from '../../../../utils/sizeSelector';

const HiddenSpacer = styled.div`
  position: relative;
  top: -52px;
  height: 0px;
  visibility: hidden;
`;

type ProductDetailsProps = {
  selectedVariant: Shopify.ProductVariant | undefined;
  setSelectedVariant: (variant: Shopify.ProductVariant) => void;
  completeTheCollectionRef: React.RefObject<HTMLDivElement | null>;
  errors: VariantSelectionError[];
  setErrors: (errors: VariantSelectionError[]) => void;
};

const AddToCartContainer: React.FC<ProductDetailsProps> = ({
  selectedVariant,
  setSelectedVariant,
  completeTheCollectionRef,
  errors,
  setErrors,
}: ProductDetailsProps) => {
  const { state, dispatch } = useStore();
  const router = useRouter();
  const { incrementSpamCounter, isSpam } = useContext(SpamFlaggingContext);
  const product = getProductFromContext();
  const productDetails = getProductDetailsFromContext();
  const { totalInventory } = productDetails;
  const totalInventoryNumber = Number(totalInventory);
  const stage = getStage(product.tags);
  const metafields = parseMetafields(product.metafields);
  const isLimited = hasProductTag(product.tags, ProductTag.Limited);
  const isHybrid = hasProductTag(product.tags, ProductTag.Hybrid);
  const cartProductQty = getQtyOfProductInCart(state?.cart?.shopifyCart, product.title);
  const hasManyVariants = product.variants.edges.length > 1;
  const isApparel = isApparelProduct(product);

  const [number, setNumber] = useState(1);

  const shoppingCartRef = useRef<HTMLDivElement | null>(null);
  const hiddenSpacerRef = useRef<HTMLDivElement | null>(null);

  const scrollToSizeSelector = () => {
    if (hiddenSpacerRef.current) {
      hiddenSpacerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleSizeChange = (size: SelectOption) => {
    const variant = findVariantBySelectOption(product.variants.edges, size);
    if (variant) {
      setSelectedVariant(variant);

      const removedError = errors.filter((error) => error.productID !== product.id);
      setErrors(removedError);

      analyticsTrackSizeSelected(product, variant, state.user, SizeSelectionLocation.PDP);
    }
  };

  useEffect(() => {
    if (router.query.scrollToSizeSelector === 'true') {
      setTimeout(() => {
        scrollToSizeSelector();
      }, 50);
    }
  }, [router.query]);

  useEffect(() => {
    setNumber(1);
  }, [product]);

  const incrementNumber = () => {
    if (isLimited) {
      if (totalInventoryNumber - (number + cartProductQty) <= 0) return;
    }
    setNumber((number || 0) + 1);
  };

  const decrementNumber = () => {
    if (number <= 1) {
      return;
    }
    if ((number || 0) > 1) {
      setNumber(number - 1);
    }
  };

  const handleFloatingAddToCart = () => {
    if (!selectedVariant) {
      scrollToSizeSelector();
    }
    handleAddToCart(CartEventLocation.StickyCartButton);
  };

  const handleAddToCart = (eventLocation?: CartEventLocation) => {
    if (selectedVariant) {
      addProduct(state.cart, number || 0, selectedVariant).then((action) => dispatch(action));
      dispatch(showCart());
      incrementSpamCounter();
      if (!isSpam) {
        analyticsTrackAddToCart(
          product,
          number || 0,
          state.user,
          eventLocation || CartEventLocation.ProductPage,
          selectedVariant,
          metafields.campaignID,
        );
        analyticsTrackViewCart(
          state.cart.shopifyCart?.lines.edges,
          state.cart.shopifyCart?.cost.subtotalAmount.amount,
          state.user,
          CartEventLocation.CartPopout,
        );
      }

      if (isLimited) {
        if (totalInventoryNumber - (number + cartProductQty) <= 0) setNumber(1);
        if (totalInventoryNumber - (2 * number + cartProductQty) <= 0) {
          const newInputNum = totalInventoryNumber - (number + cartProductQty) || 1;
          setNumber(newInputNum);
        }
      }
      return;
    }

    sendAddUnselectedVariantToCartEvent(product.title);
    const selectVariantError = addVariantSelectionErrorIfNotAlreadyPresent(
      errors,
      product.id,
      ErrorType.ApparelSizeMissing,
    );
    setErrors(selectVariantError);
  };

  return (
    <>
      {hasManyVariants && !isApparel && (
        <VariantSelector errors={errors} selectedVariant={selectedVariant} setSelectedVariant={setSelectedVariant} />
      )}
      {isApparel &&
        !isOneSizeApparelProduct(product) &&
        ((stage === ProductStage.Live && product.availableForSale) || stage === ProductStage.Draft || isHybrid) && (
          <>
            <HiddenSpacer ref={hiddenSpacerRef} />
            <SizeSelector
              sizes={variantsToSelectOptions(product.variants.edges, product)}
              selectedSize={variantToSelectOption(selectedVariant, product)}
              onSizeChange={handleSizeChange}
              error={hasApparelSizeError(product.id, errors)}
              product={product}
              disabled={!product.availableForSale}
              user={state.user}
              showSizeGuide
              className="mb-6"
            />
          </>
        )}
      {(product.availableForSale || stage === ProductStage.Draft) && (
        <>
          <AddToCartButtonFloatingContainer
            number={number}
            setNumber={setNumber}
            totalInventory={totalInventoryNumber}
            incrementNumber={incrementNumber}
            decrementNumber={decrementNumber}
            handleFloatingAddToCart={handleFloatingAddToCart}
            shoppingCartRef={shoppingCartRef}
            completeTheCollectionRef={completeTheCollectionRef}
          />
          <AddToCartButtonContainer
            number={number}
            setNumber={setNumber}
            totalInventory={totalInventoryNumber}
            incrementNumber={incrementNumber}
            decrementNumber={decrementNumber}
            handleAddToCart={handleAddToCart}
            shoppingCartRef={shoppingCartRef}
          />
        </>
      )}
    </>
  );
};

export default AddToCartContainer;
